import { log, userUtils } from '@/helpers';
import { Setter } from '@/legacy-types';
import type React from 'react';
import { create } from 'zustand';

export interface MaximizedLayer {
	content: () => React.ReactNode;
	config: MaximizeConfig | null;
	maximizedKey?: string;
	trackedParams: Set<string>; // Track query parameters specific to this layer
}

interface MaximizeStore {
	isMaximized: boolean;
	exitConfirmed?: boolean;
	setExitConfirmed: (exitConfirmed: boolean) => void;
	exitModalOpen: boolean;
	setExitModalOpen: (open: boolean) => void;

	layers: MaximizedLayer[];

	maximizedKey: string | undefined;
	maximize: (content: () => React.ReactNode, config?: MaximizeConfig & { maximizedKey?: string }) => void;
	unmaximize: (ignoreExitConfirm?: boolean, fullClose?: boolean, forceClearParams?: boolean) => void;
	navigateToLayer: (index: number, forceClearParams?: boolean) => void;

	config: MaximizeConfig | null;
	setConfig: (config: Setter<MaximizeConfig>, target?: { layerIndex?: number; maximizedKey?: string }) => void;

	getLayer: ({ layerIndex, maximizedKey }: { layerIndex?: number; maximizedKey?: string }) => MaximizedLayer | undefined;

	unmaximizeCallbacks: Array<() => void>;
	addUnmaximizeCallback: (callback: () => void, options?: { maximizedKey?: string }) => void;
	removeUnmaximizeCallback: (callback: () => void) => void;

	trackedParams: Set<string>; // Track query parameters globally (for pre-maximization)
	trackParam: (param: string | string[], maximizedKey?: string) => void;
	removeTrackedParam: (param: string, maximizedKey?: string) => void;
	getTrackedParamsForLayer: (maximizedKey?: string) => Set<string>;
}

type MaximizeConfig = {
	centerTitle?: React.ReactNode | string | (() => React.ReactNode);
	extra?: () => React.ReactNode;
	confirmExit?: boolean;
	keepRendered?: boolean; // Whether to keep this layer rendered when another layer is on top
	showBreadcrumbs?: boolean; // Whether to show breadcrumbs in the header when this is part of a layer stack
	extraFrom?: string; // Which layer to use the extra from when maximizing
};

export const useMaximizeStore = create<MaximizeStore>((set, get) => {
	// Helper function to check if a parameter is tracked by any of the remaining layers
	const isParamTrackedByRemainingLayers = (param: string, remainingLayers: MaximizedLayer[], globalParams?: Set<string>) => {
		// Check if any remaining layer tracks this param
		for (const layer of remainingLayers) {
			if (layer.trackedParams.has(param)) {
				return true;
			}
		}
		// Check if global params track this param (for pre-maximization scenarios)
		if (globalParams?.has(param)) {
			return true;
		}
		return false;
	};

	return {
		isMaximized: false,
		layers: [],
		maximizedKey: (() => {
			const params = location.search.split('maximized=');
			return params[1]?.split('&')?.[0];
		})(),
		config: null,
		unmaximizeCallbacks: [],
		trackedParams: new Set(),
		exitModalOpen: false,

		setExitConfirmed: (exitConfirmed) => {
			set({ exitConfirmed });
		},

		setExitModalOpen: (open) => {
			set({ exitModalOpen: open });
		},

		getLayer: ({ layerIndex, maximizedKey }: { layerIndex?: number; maximizedKey?: string }) => {
			if (layerIndex !== undefined) {
				return get().layers[layerIndex];
			}
			return get().layers.find((layer) => layer.maximizedKey === maximizedKey);
		},

		setConfig: (config, targeting = {}) => {
			const { layers } = get();
			const isLayerIndexDefined = targeting.layerIndex !== undefined || targeting.maximizedKey !== undefined;

			const currConfig = get().getLayer(targeting)?.config || get().config;
			const newConfig = typeof config === 'function' ? config(currConfig ?? {}) : config;

			if (layers.length > 0) {
				const actualLayer = get().getLayer(targeting);
				const layerIndex = targeting.layerIndex || layers.findIndex((layer) => actualLayer?.maximizedKey === layer.maximizedKey);

				log('setConfig', 'actualLayer', actualLayer, 'layerIndex', layerIndex, 'layers', layers, 'targeting', targeting, 'config', config);

				if (actualLayer) {
					actualLayer.config = newConfig;
					// Replace the layer index with the new config
					const updatedLayers = [...layers];
					updatedLayers[layerIndex] = actualLayer;
					set({ layers: updatedLayers });
				}
			}

			const isCurrentLayer = get().maximizedKey === targeting.maximizedKey;
			if (!isLayerIndexDefined || isCurrentLayer) {
				set({ config: newConfig });
				log('setConfig', 'isCurrentLayer', isCurrentLayer, 'config', config);
			}
		},

		maximize: (content, config?: MaximizeConfig & { maximizedKey?: string }) => {
			log('maximizeing', content, config);

			// Only allow maximizeing the same key once
			if (config?.maximizedKey) {
				const existingLayer = get().layers.find((layer) => layer.maximizedKey === config.maximizedKey);
				if (existingLayer) {
					log('Already maximized this key, ignoring');
					return;
				}
			}

			const finalConfig = config || null;
			const { layers, trackedParams: globalTrackedParams } = get();

			// Initialize layer-specific tracked params
			// If this is the first layer, transfer global params to it
			const layerTrackedParams = new Set<string>();
			if (layers.length === 0 && globalTrackedParams.size > 0) {
				// Transfer global params to the first layer
				for (const param of Array.from(globalTrackedParams)) {
					layerTrackedParams.add(param);
				}
			}

			const newLayer: MaximizedLayer = {
				content,
				config: finalConfig,
				maximizedKey: config?.maximizedKey,
				trackedParams: layerTrackedParams,
			};

			if (config?.maximizedKey) {
				const url = new URL(window.location.href);
				// Save the current state before changing the URL
				window.history.pushState({ prevUrl: window.location.href }, '', window.location.href);
				url.searchParams.set('maximized', config.maximizedKey);
				window.history.pushState({}, '', url.toString());
			}

			// Add the new layer to the stack
			const updatedLayers = [...layers, newLayer];

			// Clear global tracked params if we transferred them to the first layer
			const updatedGlobalParams = layers.length === 0 ? new Set<string>() : globalTrackedParams;

			set({
				isMaximized: true,
				layers: updatedLayers,
				maximizedKey: config?.maximizedKey,
				config: finalConfig,
				exitConfirmed: false,
				trackedParams: updatedGlobalParams,
			});

			log('maximize', 'updatedLayers', updatedLayers);
		},

		unmaximize: (ignoreExitConfirm?: boolean, fullClose?: boolean, forceClearParams?: boolean) => {
			const { layers, exitConfirmed } = get();
			const currentLayer = layers.length > 0 ? layers[layers.length - 1] : null;

			log('unmaximizeing -  ignoreExitConfirm:', ignoreExitConfirm, 'exitConfirmed:', exitConfirmed, 'currentLayer:', currentLayer);

			// Check if exit confirmation is needed for the current layer
			if (currentLayer?.config?.confirmExit && !exitConfirmed && !ignoreExitConfirm) {
				log('unmaximize', 'exitConfirmed');
				return set({ exitModalOpen: true });
			}

			// If there are multiple layers and fullClose is not true,
			// only remove the current layer and go back to the previous one
			if (layers.length > 1 && fullClose !== true) {
				// Remove the last layer
				const layerToRemove = layers[layers.length - 1];
				const updatedLayers = layers.slice(0, -1);
				const previousLayer = updatedLayers[updatedLayers.length - 1];

				// Smart parameter cleanup: only remove params that aren't tracked by remaining layers
				const url = new URL(window.location.href);
				for (const param of Array.from(layerToRemove.trackedParams)) {
					if (url.searchParams.has(param)) {
						// If forceClearParams is true, remove all params from this layer
						// Otherwise, only remove if no remaining layer tracks this param
						if (forceClearParams || !isParamTrackedByRemainingLayers(param, updatedLayers, get().trackedParams)) {
							url.searchParams.delete(param);
						}
					}
				}

				// Update URL if the previous layer has a key
				if (previousLayer.maximizedKey) {
					url.searchParams.set('maximized', previousLayer.maximizedKey);
				}

				window.history.pushState({}, '', url.toString());

				log('unmaximize', 'previousLayer', previousLayer);

				set({
					layers: updatedLayers,
					maximizedKey: previousLayer.maximizedKey,
					config: previousLayer.config,
					exitConfirmed: false,
					exitModalOpen: false,
				});

				return;
			}

			// If this is the last layer or fullClose is true, completely unmaximize
			const url = new URL(window.location.href);

			// If we're not selecting another layer, remove the 'maximized' param
			if (layers.length === 1) {
				url.searchParams.delete('maximized');
			}

			// Remove tracked params from all layers (or all if forceClearParams is true)
			if (forceClearParams) {
				// Force clear: remove all tracked params from all layers
				for (const layer of layers) {
					for (const param of Array.from(layer.trackedParams)) {
						if (url.searchParams.has(param)) {
							url.searchParams.delete(param);
						}
					}
				}
				// Also remove global tracked params
				for (const param of Array.from(get().trackedParams)) {
					if (url.searchParams.has(param)) {
						url.searchParams.delete(param);
					}
				}
			} else {
				// Smart clear: since we're closing all layers, remove all their params
				// (no need to check remaining layers since there won't be any)
				for (const layer of layers) {
					for (const param of Array.from(layer.trackedParams)) {
						if (url.searchParams.has(param)) {
							url.searchParams.delete(param);
						}
					}
				}
				// Also remove global tracked params
				for (const param of Array.from(get().trackedParams)) {
					if (url.searchParams.has(param)) {
						url.searchParams.delete(param);
					}
				}
			}

			window.history.pushState({}, '', url.toString());

			set({
				isMaximized: false,
				layers: [],
				maximizedKey: undefined,
				config: null,
				exitConfirmed: false,
				exitModalOpen: false,
				trackedParams: new Set(), // Clear global tracked params on full close
			});

			// Trigger all callbacks in order
			for (const callback of get().unmaximizeCallbacks) {
				callback();
			}

			// clear callbacks
			set({ unmaximizeCallbacks: [] });
		},

		// New function to navigate to a specific layer by index
		navigateToLayer: (index, forceClearParams = false) => {
			const { layers } = get();

			if (index < 0 || index >= layers.length) {
				return; // Invalid index
			}

			// Remove all layers above the target index
			const layersToRemove = layers.slice(index + 1);
			const updatedLayers = layers.slice(0, index + 1);
			const targetLayer = updatedLayers[index];

			// Smart parameter cleanup: only remove params that aren't tracked by remaining layers
			const url = new URL(window.location.href);
			for (const layer of layersToRemove) {
				for (const param of Array.from(layer.trackedParams)) {
					if (url.searchParams.has(param)) {
						// If forceClearParams is true, remove all params from layers being removed
						// Otherwise, only remove if no remaining layer tracks this param
						if (forceClearParams || !isParamTrackedByRemainingLayers(param, updatedLayers, get().trackedParams)) {
							url.searchParams.delete(param);
						}
					}
				}
			}

			// Update URL if the target layer has a key
			if (targetLayer.maximizedKey) {
				url.searchParams.set('maximized', targetLayer.maximizedKey);
			}

			window.history.pushState({}, '', url.toString());

			set({
				layers: updatedLayers,
				maximizedKey: targetLayer.maximizedKey,
				config: targetLayer.config,
				exitConfirmed: false,
			});
		},

		addUnmaximizeCallback: (callback) => {
			set((state) => {
				if (!state.unmaximizeCallbacks.includes(callback)) {
					return { unmaximizeCallbacks: [...state.unmaximizeCallbacks, callback] };
				}
				return state;
			});
		},

		removeUnmaximizeCallback: (callback) => {
			set((state) => ({
				unmaximizeCallbacks: state.unmaximizeCallbacks.filter((cb) => cb !== callback),
			}));
		},

		trackParam: (param, maximizedKey) => {
			set((state) => {
				const paramsToAdd = Array.isArray(param) ? param : [param];

				// If maximizedKey is provided, target that specific layer
				if (maximizedKey) {
					const layerIndex = state.layers.findIndex((layer) => layer.maximizedKey === maximizedKey);
					if (layerIndex !== -1) {
						const targetLayer = state.layers[layerIndex];
						// Create a new Set with the added params
						const newTrackedParams = new Set(targetLayer.trackedParams);
						for (const p of paramsToAdd) {
							newTrackedParams.add(p);
						}
						// Create a completely new layer object
						const updatedLayer: MaximizedLayer = {
							...targetLayer,
							trackedParams: newTrackedParams,
						};
						// Update the layers array with the new layer
						const updatedLayers = [...state.layers];
						updatedLayers[layerIndex] = updatedLayer;
						return { layers: updatedLayers };
					}
					// If layer not found, fall back to global tracking
				}

				// If no maximizedKey provided, target current layer if exists
				if (!maximizedKey && state.layers.length > 0) {
					const currentLayerIndex = state.layers.length - 1;
					const currentLayer = state.layers[currentLayerIndex];
					// Create a new Set with the added params
					const newTrackedParams = new Set(currentLayer.trackedParams);
					for (const p of paramsToAdd) {
						newTrackedParams.add(p);
					}
					// Create a completely new layer object
					const updatedLayer: MaximizedLayer = {
						...currentLayer,
						trackedParams: newTrackedParams,
					};
					// Update the layers array with the new layer
					const updatedLayers = [...state.layers];
					updatedLayers[currentLayerIndex] = updatedLayer;
					return { layers: updatedLayers };
				}

				// If no layers exist or layer not found, add to global tracking (pre-maximization)
				const updatedParams = new Set(state.trackedParams);
				for (const p of paramsToAdd) {
					updatedParams.add(p);
				}
				return { trackedParams: updatedParams };
			});
		},

		removeTrackedParam: (param, maximizedKey) => {
			set((state) => {
				// If maximizedKey is provided, target that specific layer
				if (maximizedKey) {
					const layerIndex = state.layers.findIndex((layer) => layer.maximizedKey === maximizedKey);
					if (layerIndex !== -1) {
						const targetLayer = state.layers[layerIndex];
						// Create a new Set with the param removed
						const newTrackedParams = new Set(targetLayer.trackedParams);
						newTrackedParams.delete(param);
						// Create a completely new layer object
						const updatedLayer: MaximizedLayer = {
							...targetLayer,
							trackedParams: newTrackedParams,
						};
						// Update the layers array with the new layer
						const updatedLayers = [...state.layers];
						updatedLayers[layerIndex] = updatedLayer;
						return { layers: updatedLayers };
					}
					// If layer not found, fall back to global removal
				}

				// If no maximizedKey provided, target current layer if exists
				if (!maximizedKey && state.layers.length > 0) {
					const currentLayerIndex = state.layers.length - 1;
					const currentLayer = state.layers[currentLayerIndex];
					// Create a new Set with the param removed
					const newTrackedParams = new Set(currentLayer.trackedParams);
					newTrackedParams.delete(param);
					// Create a completely new layer object
					const updatedLayer: MaximizedLayer = {
						...currentLayer,
						trackedParams: newTrackedParams,
					};
					// Update the layers array with the new layer
					const updatedLayers = [...state.layers];
					updatedLayers[currentLayerIndex] = updatedLayer;
					return { layers: updatedLayers };
				}

				// If no layers exist or layer not found, remove from global tracking
				const updatedParams = new Set(state.trackedParams);
				updatedParams.delete(param);
				return { trackedParams: updatedParams };
			});
		},

		getTrackedParamsForLayer: (maximizedKey) => {
			const { layers } = get();

			// If maximizedKey is provided, find that specific layer
			if (maximizedKey) {
				const layer = layers.find((layer) => layer.maximizedKey === maximizedKey);
				return layer?.trackedParams || new Set();
			}

			// If no maximizedKey provided, return current layer's params
			if (layers.length > 0) {
				const currentLayer = layers[layers.length - 1];
				return currentLayer.trackedParams;
			}

			// If no layers exist, return global tracked params
			return get().trackedParams;
		},
	};
});
